/**
 * Admin Diagnostics — LEGACY view.
 *
 * Retired (Diagnostics Phase 3, 2026-02). The Pipeline / Migrations /
 * Pre-flight diagnostic cards that used to live here are gone — those
 * three diagnostics now run through the unified registry on the main
 * /admin/diagnostics page (check ids data_integrity.crm_leads,
 * deploy_state.migrations, deploy_state.migrations_preflight). Their
 * HTTP endpoints return 410 Gone.
 *
 * The ONLY thing that stays here is the ArchivedBatchesCard — read-only
 * records of the original token-gated Batch A/C/D migration runners.
 * The owner's runbook references those batches by name; the per-member
 * applied dates remain visible so historical apply records are not
 * lost. Everything else was deduplicated.
 *
 * Auth: super-admin only (backend 404s non-super-admins).
 */
import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, Loader2, Activity, BarChart3 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminDiagnosticsLegacy() {
  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6" data-testid="admin-diagnostics-page">
      <div className="flex items-center gap-3">
        <Activity className="w-6 h-6 text-amber-600" />
        <div>
          <h1 className="text-2xl font-semibold">Admin Diagnostics · Legacy</h1>
          <p className="text-sm text-slate-500">
            Retired view. The Pipeline / Migrations / Pre-flight diagnostics now run
            through the unified console on{' '}
            <a href="/admin/diagnostics" className="underline text-[#BC5D41]">/admin/diagnostics</a>.
            Only archived Batch A/C/D runner records remain here.
          </p>
        </div>
      </div>

      <ArchivedBatchesCard />
    </div>
  );
}

/**
 * Archived records of the retired token-gated Batch A/C/D migration
 * runners. Read-only.
 */
function ArchivedBatchesCard() {
  const [state, setState] = useState({ loading: false, data: null, error: null });

  const load = async () => {
    setState({ loading: true, data: null, error: null });
    try {
      const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
      const res = await axios.get(`${API}/api/admin/migrations/batches/archive`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setState({ loading: false, data: res.data, error: null });
    } catch (err) {
      const msg = err.response?.status === 404
        ? 'Access denied — super-admin only.'
        : (err.response?.data?.detail || err.message || 'Load failed');
      setState({ loading: false, data: null, error: msg });
    }
  };

  return (
    <Card className="border-slate-300 bg-slate-50" data-testid="archived-batches-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-slate-700">
          <BarChart3 className="w-5 h-5 text-slate-500" />
          Archived: Batch A / C / D migration runners
          <Badge variant="outline" className="bg-slate-200 text-slate-600 border-slate-300">Retired</Badge>
        </CardTitle>
        <CardDescription>
          These token-gated batch runners are retired and kept as records only. All
          migrations — past and future — now run through the unified <strong>Migrations</strong> card
          on the main diagnostics view (super-admin + typed confirmation, no tokens).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <Button variant="outline" onClick={load} disabled={state.loading} data-testid="archived-batches-load-btn">
          {state.loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          {state.loading ? 'Loading…' : 'Show batch records'}
        </Button>
        {state.error && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-800">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>{state.error}</span>
          </div>
        )}
        {state.data && (
          <div className="space-y-3">
            {state.data.batches.map((b) => (
              <div key={b.batch} className="p-3 rounded-lg border border-slate-200 bg-white" data-testid={`archived-batch-${b.batch}`}>
                <div className="flex items-center gap-2 mb-1">
                  <Badge variant="outline" className="font-mono">Batch {b.batch}</Badge>
                  <span className="text-sm font-medium text-slate-700">{b.scope}</span>
                </div>
                <p className="text-xs text-slate-500 mb-2">{b.retired_note}</p>
                <div className="flex flex-wrap gap-1.5">
                  {b.members.map((m) => (
                    <span
                      key={m.name}
                      title={m.applied_at ? `Applied ${m.applied_at.slice(0, 19).replace('T', ' ')}` : 'Not applied'}
                      className={`text-[11px] px-1.5 py-0.5 rounded border font-mono ${m.status === 'applied'
                        ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                        : 'bg-amber-50 border-amber-200 text-amber-800'}`}
                    >
                      {m.name} {m.status === 'applied' ? '✓' : '· pending'}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
